import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Smartphone, CreditCard, LayoutDashboard, Phone } from "lucide-react";
import { waLink, WA_DEMO_MSG, PHONE_LINK, WA_NUMBER_DISPLAY } from "@/lib/wa";
import { STR } from "@/data/i18n";
import { useLang } from "@/lib/lang";
import { WhatsAppIcon } from "./Shared";
import { EmberCanvas } from "./EmberCanvas";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className={`block ${className}`}
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const BADGE_ICONS = [Smartphone, CreditCard, LayoutDashboard];

export const Hero = () => {
    const sectionRef = useRef(null);
    const { lang } = useLang();
    const t = STR.hero[lang];
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const contentY = useTransform(scrollYProgress, [0, 1], [0, 120]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section
            id="beranda"
            ref={sectionRef}
            data-testid="hero-section"
            className="relative flex min-h-[92vh] flex-col justify-center overflow-hidden pt-24 pb-16"
        >
            <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1758612853656-def5033bccb5?auto=format&fit=crop&w=1920&q=80')",
                }}
                aria-hidden="true"
            />
            <div className="absolute inset-0 bg-black/75" aria-hidden="true" />
            <EmberCanvas />
            <div
                className="absolute -top-32 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-orange-600/20 blur-[120px]"
                aria-hidden="true"
            />

            <motion.div
                style={{ y: contentY, opacity: contentOpacity }}
                className="relative z-10 mx-auto w-full max-w-5xl px-5 text-center md:px-8"
            >
                <motion.div
                    key={`badge-${lang}`}
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.7, ease: EASE }}
                >
                    <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-amber-300">
                        {t.badge}
                    </span>
                </motion.div>

                <h1 className="mt-6 text-4xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
                    <MaskedLine key={`l1-${lang}`} delay={0.15}>{t.line1}</MaskedLine>
                    <MaskedLine key={`l2-${lang}`} delay={0.3} className="text-gradient">
                        {t.line2}
                    </MaskedLine>
                    <MaskedLine key={`l3-${lang}`} delay={0.45} className="text-gradient">
                        {t.line3}
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
                    className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-zinc-300 md:text-lg"
                >
                    {t.sub}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.9, ease: EASE }}
                    className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
                >
                    <motion.a
                        href={waLink(WA_DEMO_MSG)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-cta-demo"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.96 }}
                        className="btn-flame inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-semibold text-white shadow-xl shadow-orange-900/50 sm:w-auto"
                    >
                        <WhatsAppIcon className="h-5 w-5" />
                        {t.ctaDemo}
                    </motion.a>
                    <motion.a
                        href="/#paket"
                        data-testid="hero-cta-pricing"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.96 }}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-8 py-4 text-base font-semibold text-slate-100 backdrop-blur transition-colors duration-200 hover:border-amber-400/50 hover:text-amber-300 sm:w-auto"
                    >
                        {t.ctaPricing}
                    </motion.a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.05 }}
                    className="mt-6"
                >
                    <a
                        href={PHONE_LINK}
                        data-testid="hero-cta-phone"
                        className="inline-flex items-center gap-2 text-sm font-medium text-zinc-300 transition-colors duration-200 hover:text-amber-400"
                    >
                        <Phone className="h-4 w-4 text-amber-400" />
                        {t.phone} {WA_NUMBER_DISPLAY}
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.15 }}
                    className="mt-12 flex flex-wrap items-center justify-center gap-x-10 gap-y-4"
                >
                    {t.badges.map((label, i) => {
                        const Icon = BADGE_ICONS[i];
                        return (
                            <div key={label} className="flex items-center gap-2 text-sm text-zinc-300">
                                <Icon className="h-4 w-4 text-amber-400" />
                                {label}
                            </div>
                        );
                    })}
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.8 }}
                data-testid="hero-scroll-indicator"
                className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2"
                aria-hidden="true"
            >
                <div className="flex h-10 w-6 justify-center rounded-full border border-white/20 pt-2">
                    <motion.span
                        animate={{ y: [0, 10, 0], opacity: [1, 0.3, 1] }}
                        transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                        className="h-1.5 w-1.5 rounded-full bg-amber-400"
                    />
                </div>
            </motion.div>
        </section>
    );
};
